import { logger } from '@curology/logger/client';
import { type FileRejection } from '@curology/ui-components-web';

import { type Brand } from '@/consts/brands';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { type PhotoResource } from '@/services/funnelService/types';
import { PhotoUploadService } from '@/services/photoUploadService/client';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import {
  FILE_SIZE_ERROR_MESSAGE,
  MAX_FILE_SIZE_BYTES,
} from '@/utils/images/fileSizeLimits';
import { heicConverter } from '@/utils/images/heic';
import { HEIC_MIME_TYPES } from '@/utils/images/types';

import { shouldDisableSubmitButton } from './helpers';
import { type QuestionPhotoUploadModel } from '../../../config/questions/photoUploadQuestion';
import { type PhotoUploadFile } from '../types/types';

const HEIC_CONVERSION_ERROR_MESSAGE =
  'We were unable to convert your photo. Please try again with a JPEG or PNG image.';

export const handleFileAdded = async (
  brand: Brand,
  files: File[],
  photo: PhotoResource,
  question: QuestionPhotoUploadModel,
  requiredPhotoTypes: string[],
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setUploadedFiles: React.Dispatch<React.SetStateAction<PhotoUploadFile[]>>,
  setUploading: React.Dispatch<React.SetStateAction<boolean>>,
  treatmentCategory?: TreatmentCategory,
  onError?: (error: Error) => void,
) => {
  let file = files[0];

  if (file.size > MAX_FILE_SIZE_BYTES) {
    onError?.(new Error(FILE_SIZE_ERROR_MESSAGE));
    return;
  }

  setUploading(true);

  const data = new FormData();

  if (HEIC_MIME_TYPES.includes(file.type)) {
    try {
      file = await heicConverter(file);
    } catch (error) {
      logger.error(error, {
        message: 'Survey photo HEIC conversion failed',
        fileName: file.name,
      });
      onError?.(new Error(HEIC_CONVERSION_ERROR_MESSAGE));
      setUploading(false);
      return;
    }
  }

  data.append('file', file);

  if (photo.isForProvider) {
    data.append('isForProvider', 'true');
  }

  data.append(
    'metadata',
    JSON.stringify({
      photo_perspective: photo.type,
      treatment_category: treatmentCategory,
    }),
  );

  if (photo.isIdVerification) {
    data.append('isIdVerification', 'true');
  }

  const response = await PhotoUploadService.uploadPhotos(brand, data);
  question.value = response;

  if (response?.photo) {
    const newPhoto = {
      file: response.photo.localUrl,
      photoId: response.photo.id,
      photoType: photo.type,
    };

    setUploadedFiles((prev) => {
      const updated = [...prev, newPhoto];
      setSubmitButtonDisabled(
        shouldDisableSubmitButton(requiredPhotoTypes, updated),
      );
      return updated;
    });
  }

  setUploading(false);

  trackEvent(AnalyticsEvent.PHOTO_UPLOADED, {
    type: photo.type,
  });
};

export const handleFileDeleted = async (
  brand: Brand,
  photoId: number,
  requiredPhotoTypes: string[],
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setUploadedFiles: React.Dispatch<React.SetStateAction<PhotoUploadFile[]>>,
) => {
  let deletedPhotoType: string | undefined;

  setUploadedFiles((prev) => {
    const toDelete = prev.find(
      (uploadedFile) => uploadedFile.photoId === photoId,
    );
    deletedPhotoType = toDelete?.photoType;

    const updated = prev.filter(
      (uploadedFile) => uploadedFile.photoId !== photoId,
    );
    setSubmitButtonDisabled(
      shouldDisableSubmitButton(requiredPhotoTypes, updated),
    );
    return updated;
  });

  await PhotoUploadService.deletePhoto(brand, photoId);

  trackEvent(AnalyticsEvent.PHOTO_REMOVED, {
    type: deletedPhotoType,
  });
};

export const handleFileRejected = (
  fileRejections: FileRejection[],
  setError: React.Dispatch<React.SetStateAction<Error | undefined>>,
) => {
  logger.log('Signup Photo Rejected', fileRejections);

  setError(new Error(fileRejections?.[0].errors[0].message));
};
